import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Copy, SlidersHorizontal, Bell, Webhook, ShieldCheck, Database } from 'lucide-react';

const inputClass =
  'w-full rounded-lg border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-text-secondary';
const checkboxClass = 'size-4 cursor-pointer rounded border-border-strong accent-primary';

function SettingsCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof SlidersHorizontal;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <div className="flex items-start gap-3 p-6">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-text-title">{title}</h2>
          <p className="mt-0.5 text-sm text-text-secondary">{description}</p>
        </div>
      </div>
      <div className="flex flex-col gap-4 border-t border-border p-6">{children}</div>
      <div className="flex justify-end border-t border-border bg-muted/40 p-4">
        <Button>Salvar Alterações</Button>
      </div>
    </div>
  );
}

export function Settings() {
  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Configurações' }]}>
      <div className="p-3 sm:p-4 lg:p-8">
        <div className="flex flex-col gap-4 lg:gap-8">
          {/* Cabeçalho */}
          <div className="flex flex-col gap-1">
            <h1 className="max-md:hidden text-2xl font-semibold tracking-tight text-text-title sm:text-3xl">
              Configurações
            </h1>
            <p className="text-sm text-text-secondary">
              Ajuste o sistema, notificações, integrações e políticas de dados.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start lg:gap-8">
            {/* Coluna 1 */}
            <div className="flex w-full flex-col gap-4 lg:gap-8">
              {/* Configurações Gerais */}
              <SettingsCard
                icon={SlidersHorizontal}
                title="Configurações Gerais"
                description="Atualize o nome, logo e timezone do seu sistema."
              >
                <div>
                  <label className={labelClass} htmlFor="system-name">
                    Nome do sistema
                  </label>
                  <input
                    className={inputClass}
                    id="system-name"
                    type="text"
                    defaultValue="CURRAL TECH"
                  />
                </div>
                <div>
                  <label className={labelClass}>Logo do sistema</label>
                  <div className="flex items-center gap-4">
                    <img
                      src="/logo.png"
                      alt="Logo"
                      className="size-16 shrink-0 rounded-lg border border-border bg-bg-elevated object-contain p-1.5"
                    />
                    <input
                      className="block w-full text-sm text-text-secondary file:mr-4 file:rounded-lg file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20"
                      id="logo-upload"
                      type="file"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass} htmlFor="timezone">
                    Timezone padrão
                  </label>
                  <select className={inputClass} id="timezone">
                    <option>(GMT-03:00) America/Sao_Paulo</option>
                    <option>(GMT-05:00) Eastern Time (US &amp; Canada)</option>
                    <option>(GMT+00:00) Coordinated Universal Time</option>
                  </select>
                </div>
              </SettingsCard>

              {/* Notificações */}
              <SettingsCard
                icon={Bell}
                title="Notificações"
                description="Configure como e quando você recebe alertas."
              >
                <div>
                  <label className={labelClass} htmlFor="alert-email">
                    Email para alertas críticos
                  </label>
                  <input
                    className={inputClass}
                    id="alert-email"
                    type="email"
                    defaultValue="admin@curral.tech"
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="telegram-token">
                    Telegram bot token <span className="text-xs text-text-hint">(opcional)</span>
                  </label>
                  <input
                    className={inputClass}
                    id="telegram-token"
                    placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    type="text"
                  />
                </div>
                <div className="space-y-3 pt-1">
                  <label className={labelClass}>Configurar quando alertar</label>
                  {[
                    { id: 'critical-error', label: 'Erro crítico', checked: true },
                    { id: 'device-offline', label: 'Dispositivo offline', checked: true },
                    { id: 'new-user', label: 'Novo usuário cadastrado', checked: false },
                  ].map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <input
                        defaultChecked={item.checked}
                        className={checkboxClass}
                        id={item.id}
                        type="checkbox"
                      />
                      <label className="text-sm font-medium text-text-title" htmlFor={item.id}>
                        {item.label}
                      </label>
                    </div>
                  ))}
                </div>
              </SettingsCard>

              {/* API & Webhooks */}
              <SettingsCard
                icon={Webhook}
                title="API & Webhooks"
                description="Gerencie suas chaves de API e endpoints."
              >
                <div>
                  <label className={labelClass} htmlFor="api-key">
                    API Key
                  </label>
                  <div className="relative">
                    <input
                      className="w-full rounded-lg border border-border-strong bg-bg-elevated px-3 py-2 pr-12 font-mono text-xs text-text-secondary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      id="api-key"
                      readOnly
                      type="text"
                      defaultValue="CT-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                    <button
                      aria-label="Copiar API key"
                      className="absolute inset-y-0 right-0 flex items-center justify-center rounded-r-lg px-3 text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className={labelClass} htmlFor="webhooks">
                    Webhook endpoints customizados
                  </label>
                  <textarea
                    className={inputClass}
                    id="webhooks"
                    placeholder={
                      'https://example.com/webhook/new-device\nhttps://example.com/webhook/critical-error'
                    }
                    rows={3}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="rate-limit">
                    Rate limiting (requests/min)
                  </label>
                  <input className={inputClass} id="rate-limit" type="number" defaultValue={120} />
                </div>
              </SettingsCard>
            </div>

            {/* Coluna 2 */}
            <div className="flex w-full flex-col gap-4 lg:gap-8">
              {/* Autenticação */}
              <SettingsCard
                icon={ShieldCheck}
                title="Autenticação"
                description="Defina as políticas de sessão e login para os usuários."
              >
                <div>
                  <label className={labelClass} htmlFor="otp-duration">
                    Duração do OTP (minutos)
                  </label>
                  <input className={inputClass} id="otp-duration" type="number" defaultValue={15} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="otp-attempts">
                    Tentativas máximas de OTP
                  </label>
                  <input className={inputClass} id="otp-attempts" type="number" defaultValue={3} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="jwt-duration">
                    Duração da sessão JWT (dias)
                  </label>
                  <input className={inputClass} id="jwt-duration" type="number" defaultValue={30} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="n8n-webhook">
                    Webhook N8N (URL para envio de OTP)
                  </label>
                  <input
                    className={inputClass}
                    id="n8n-webhook"
                    type="text"
                    defaultValue="https://n8n.curral.tech/webhook/send-otp"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <span className="relative flex size-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75" />
                      <span className="relative inline-flex size-2.5 rounded-full bg-online" />
                    </span>
                    <p className="text-xs font-medium text-success">
                      Status de integração: Conectado
                    </p>
                  </div>
                </div>
              </SettingsCard>

              {/* Retenção de Dados */}
              <SettingsCard
                icon={Database}
                title="Retenção de Dados"
                description="Gerencie por quanto tempo os dados são mantidos no sistema."
              >
                <div>
                  <label className={labelClass} htmlFor="logs-retention">
                    Logs (manter por X dias)
                  </label>
                  <input
                    className={inputClass}
                    id="logs-retention"
                    type="number"
                    defaultValue={90}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="errors-retention">
                    Erros resolvidos (manter por X dias)
                  </label>
                  <input
                    className={inputClass}
                    id="errors-retention"
                    type="number"
                    defaultValue={180}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="sessions-retention">
                    Sessões expiradas (manter por X dias)
                  </label>
                  <input
                    className={inputClass}
                    id="sessions-retention"
                    type="number"
                    defaultValue={30}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="offline-devices-retention">
                    Dispositivos offline (marcar como inativo após X dias)
                  </label>
                  <input
                    className={inputClass}
                    id="offline-devices-retention"
                    type="number"
                    defaultValue={60}
                  />
                </div>
              </SettingsCard>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
